(() => {
  const SECTION = '.oddit-section-featured-reviews';
  const WRAPPER = '.oddit-section-featured-reviews__track-wrapper';
  const CARD = '.oddit-section-featured-reviews__card';
  const DOTS = '.oddit-section-featured-reviews__dots';
  const DOT = 'oddit-section-featured-reviews__dot';

  function initSection(section) {
    const wrapper = section.querySelector(WRAPPER);
    if (!wrapper) return;
    const dotsEl = section.querySelector(DOTS);

    const ac = new AbortController();
    const signal = ac.signal;
    section.__reviewsAbort = ac;

    let stops = [];
    let dragActive = false;
    let dragMoved = false;
    let dragStartX = 0;
    let dragLastX = 0;
    let syncQueued = false;
    let autoplayTimer = null;

    const autoplayEnabled = section.dataset.autoplay === 'true';
    const autoplaySpeed = (parseInt(section.dataset.autoplaySpeed, 10) || 5) * 1000;

    const measure = () => {
      const cards = section.querySelectorAll(CARD);
      const pad = parseFloat(getComputedStyle(wrapper).scrollPaddingLeft) || 0;
      const max = Math.max(0, wrapper.scrollWidth - wrapper.clientWidth);
      const origin = wrapper.getBoundingClientRect().left - wrapper.scrollLeft + pad;

      stops = [];
      cards.forEach((card) => {
        const pos = card.getBoundingClientRect().left - origin;
        const target = Math.round(Math.min(Math.max(pos, 0), max));
        if (!stops.some((s) => Math.abs(s - target) <= 1)) stops.push(target);
      });
      stops.sort((a, b) => a - b);
    };

    const activeIndex = () => {
      let best = 0;
      stops.forEach((s, i) => {
        if (Math.abs(s - wrapper.scrollLeft) < Math.abs(stops[best] - wrapper.scrollLeft)) best = i;
      });
      return best;
    };

    const paintDots = () => {
      if (!dotsEl) return;
      const active = activeIndex();
      [...dotsEl.children].forEach((dot, i) => {
        const on = i === active;
        dot.classList.toggle(`${DOT}--active`, on);
        dot.setAttribute('aria-selected', String(on));
      });
    };

    const buildDots = () => {
      if (!dotsEl) return;
      dotsEl.hidden = stops.length < 2;
      dotsEl.textContent = '';
      stops.forEach((_, i) => {
        const dot = document.createElement('button');
        dot.type = 'button';
        dot.className = DOT;
        dot.setAttribute('role', 'tab');
        dot.setAttribute('aria-label', `Go to review group ${i + 1} of ${stops.length}`);
        dot.addEventListener('click', () => {
          wrapper.scrollTo({ left: stops[i], behavior: 'smooth' });
          restartAutoplay();
        });
        dotsEl.appendChild(dot);
      });
      paintDots();
    };

    const settle = () => {
      if (!stops.length) return;
      const nearest = stops.reduce((a, b) =>
        Math.abs(b - wrapper.scrollLeft) < Math.abs(a - wrapper.scrollLeft) ? b : a
      );
      wrapper.scrollTo({ left: nearest, behavior: 'smooth' });
    };

    // --- Autoplay ---
    const advanceSlide = () => {
      if (!stops.length) return;
      const idx = activeIndex();
      const next = (idx + 1) % stops.length;
      wrapper.scrollTo({ left: stops[next], behavior: 'smooth' });
    };

    const startAutoplay = () => {
      if (!autoplayEnabled || stops.length < 2) return;
      stopAutoplay();
      autoplayTimer = setInterval(advanceSlide, autoplaySpeed);
    };

    const stopAutoplay = () => {
      if (autoplayTimer) {
        clearInterval(autoplayTimer);
        autoplayTimer = null;
      }
    };

    const restartAutoplay = () => {
      stopAutoplay();
      startAutoplay();
    };

    // Pause on hover / focus
    section.addEventListener('mouseenter', stopAutoplay, { signal });
    section.addEventListener('mouseleave', startAutoplay, { signal });
    section.addEventListener('focusin', stopAutoplay, { signal });
    section.addEventListener('focusout', startAutoplay, { signal });

    wrapper.addEventListener(
      'scroll',
      () => {
        if (syncQueued) return;
        syncQueued = true;
        requestAnimationFrame(() => {
          syncQueued = false;
          paintDots();
        });
      },
      { passive: true, signal }
    );

    // Mouse drag — touch keeps native scroll-snap momentum.
    wrapper.addEventListener('pointerdown', (e) => {
      if (e.pointerType && e.pointerType !== 'mouse') return;
      if (typeof e.button === 'number' && e.button !== 0) return;
      dragActive = true;
      dragMoved = false;
      dragStartX = e.clientX;
      dragLastX = e.clientX;
      wrapper.classList.add('is-dragging');
      stopAutoplay();
    }, { signal });

    window.addEventListener('pointermove', (e) => {
      if (!dragActive) return;
      const dx = e.clientX - dragLastX;
      dragLastX = e.clientX;
      if (Math.abs(e.clientX - dragStartX) > 4) dragMoved = true;
      wrapper.scrollLeft -= dx;
    }, { signal });

    const endDrag = () => {
      if (!dragActive) return;
      dragActive = false;
      wrapper.classList.remove('is-dragging');
      if (dragMoved) settle();
      restartAutoplay();
    };

    window.addEventListener('pointerup', endDrag, { signal });
    window.addEventListener('pointercancel', endDrag, { signal });

    // Swallow the click that ends a drag so links/cards don't activate.
    wrapper.addEventListener(
      'click',
      (e) => {
        if (!dragMoved) return;
        e.preventDefault();
        e.stopPropagation();
        dragMoved = false;
      },
      { capture: true, signal }
    );

    let resizeQueued = false;
    window.addEventListener('resize', () => {
      if (resizeQueued) return;
      resizeQueued = true;
      requestAnimationFrame(() => {
        resizeQueued = false;
        measure();
        buildDots();
        restartAutoplay();
      });
    }, { signal });

    measure();
    buildDots();
    startAutoplay();
  }

  function destroySection(section) {
    if (section.__reviewsAbort) {
      section.__reviewsAbort.abort();
      delete section.__reviewsAbort;
    }
  }

  // Initial page load
  document.querySelectorAll(SECTION).forEach(initSection);

  // Theme editor compatibility
  document.addEventListener('shopify:section:load', (e) => {
    const section = e.target.querySelector(SECTION);
    if (section) initSection(section);
  });

  document.addEventListener('shopify:section:unload', (e) => {
    const section = e.target.querySelector(SECTION);
    if (section) destroySection(section);
  });
})();
